'use client';

import { format } from 'date-fns';

// @mui
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

// locales
import { useTranslate } from 'src/locales';
// hooks
// types
import { useRouter } from 'next/navigation';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';

// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { useBoolean } from '../../../../../hooks/use-boolean';
import { ConfirmDialog } from '../../../../../components/custom-dialog';
//
// import CustomerQuickEditForm from './customer-quick-edit-form';

// ----------------------------------------------------------------------

type Props = {
  selected: boolean;
  onEditRow: VoidFunction;
  row: any;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
  page?: string;
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '100%', md: 600 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

export default function ProfileMyAgentTableRow({
  row,
  selected,
  onEditRow,
  onSelectRow,
  onDeleteRow,
  page,
}: Props) {
  const { t } = useTranslate();

  const { id, agentsName, smbAccount, activity, date, status, creator } = row;

  const confirm = useBoolean();

  const quickEdit = useBoolean();

  const popover = usePopover();

  const router = useRouter();

  const openDetails = useBoolean();

  const handleOpenDetails = useBoolean();

  const handleDetails = () => {
    // router.push(paths.dashboard.affiliates.agents.details(id));
  };

  const formattedDate = row?.date
    ? new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(row?.date))
    : '-';

  return (
    <>
      <TableRow hover selected={selected} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={handleOpenDetails.onTrue}>
          {agentsName}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={openDetails.onTrue}>
          {smbAccount}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={openDetails.onTrue}>
          {activity}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <ListItemText
            primary={format(new Date(date), 'dd.MM.yyyy')}
            secondary={format(new Date(date), 'HH:mm a')}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{
              fontSize: 12,
              component: 'p',
              color: 'text.disabled',
            }}
          />
        </TableCell>

        <TableCell>
          <Label
            variant="soft"
            color={
              (status === 'active' && 'success') ||
              (status === 'pending' && 'warning') ||
              (status === 'banned' && 'error') ||
              'default'
            }
          >
            {status}
          </Label>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={openDetails.onTrue}>
          {creator}
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            confirm.onTrue();
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          {t('Delete')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            onEditRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          {t('Edit')}
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('Delete')}
        content={t('Are you sure want to delete?')}
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            {t('Delete')}
          </Button>
        }
      />

      <Modal open={handleOpenDetails.value} onClose={handleOpenDetails.onFalse}>
        <Box sx={style}>
          <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
            <IconButton onClick={handleOpenDetails.onFalse}>
              <Iconify icon="material-symbols:close-rounded" />
            </IconButton>
          </Box>
          <Container maxWidth="md">
            <Grid container spacing={{ xs: 1, md: 1, lg: 1 }}>
              <Grid xs={6} md={6} lg={5}>
                <Typography>Agents name:</Typography>
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                {row?.agentsName}
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                <Typography>Activity:</Typography>
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                {row?.activity}
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                <Typography>Creator:</Typography>
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                {row?.creator}
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                <Typography>SmbAccount:</Typography>
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                {row?.smbAccount}
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                <Typography>Status:</Typography>
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                {row?.status}
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                <Typography>Date:</Typography>
              </Grid>
              <Grid xs={6} md={6} lg={5}>
                {formattedDate}
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Modal>
    </>
  );
}
